import type { PaymentSystem } from './payment';
import type { StudentClassification } from './studentClassification';
import type { ProjectIdentity } from './project';
import type { ActiveSubscriptionPause } from './subscription';

/* ─── Enums ─────────────────────────────────────────────── */

export enum PaymentStatusEnum {
  PAID = 'PAID',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
  PAUSED = 'PAUSED',
  SUBSCRIPTION_CANCELED = 'SUBSCRIPTION_CANCELED',
}

export enum WorkoutStatusEnum {
  AWAITING_PAYMENT = 'awaiting_payment',
  PAYMENT_FAILED = 'payment_failed',
  READY_FOR_WORKOUT = 'ready_for_workout',
  AWAITING_FUP_RESPONSE = 'awaiting_fup_response',
  SCHEDULED = 'scheduled',
  DELIVERED = 'delivered',
  CANCELED = 'canceled',
  PAUSED = 'paused',
}

export enum DatePresetEnum {
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last7',
  LAST_30_DAYS = 'last30',
  CUSTOM = 'custom',
}

export type WorkoutStatus = 'awaiting_payment' | 'payment_failed' | 'ready_for_workout' | 'awaiting_fup_response' | 'scheduled' | 'delivered' | 'canceled' | 'paused';

export type PaymentStatus = `${PaymentStatusEnum}`;

export interface FupLink {
  token: string;
  attempt_number: number;
  created_at: string;
  responded_at: string | null;
}

export interface StudentNoteItem {
  id: number;
  note: string;
  created_at: string;
  author_name: string;
  portal_user_id: number;
  can_delete: boolean;
}

/* ─── Filter options (reusable in UI) ───────────────────── */

export const PAYMENT_STATUS_OPTIONS = [
  { value: PaymentStatusEnum.PAID, label: 'Pago' },
  { value: PaymentStatusEnum.PENDING, label: 'Pendente' },
  { value: PaymentStatusEnum.EXPIRED, label: 'Vencido' },
  { value: PaymentStatusEnum.PAUSED, label: 'Pausada' },
  { value: PaymentStatusEnum.SUBSCRIPTION_CANCELED, label: 'Assinatura cancelada' },
] as const;

export const PAYMENT_METHOD_OPTIONS = [
  { value: 'pix', label: 'Pix' },
  { value: 'credit_card', label: 'Cartão de crédito' },
] as const;

export const PAYMENT_METHOD_LABELS: Record<(typeof PAYMENT_METHOD_OPTIONS)[number]['value'], string> = {
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
};

export const NO_COACH_FILTER_VALUE = 'none';

export const WORKOUT_STATUS_OPTIONS = [
  { value: WorkoutStatusEnum.READY_FOR_WORKOUT, label: 'Pronto para treino' },
  { value: WorkoutStatusEnum.SCHEDULED, label: 'Enviado para Performance' },
  { value: WorkoutStatusEnum.DELIVERED, label: 'Entregue' },
  { value: WorkoutStatusEnum.CANCELED, label: 'Cancelado' },
  { value: WorkoutStatusEnum.AWAITING_FUP_RESPONSE, label: 'Aguardando FUP' },
] as const;

export const FUP_STATUS_FILTER_OPTIONS = [
  { value: 'not_sent', label: 'Não enviados' },
  { value: 'unanswered', label: 'Não respondidos', color: 'var(--color-solid-danger)' },
  { value: 'pending', label: 'Pendentes', color: 'var(--color-solid-warning)' },
  { value: 'answered', label: 'Respondidos', color: 'var(--color-solid-success)' },
] as const;

export type OrchestratorFupStatus = (typeof FUP_STATUS_FILTER_OPTIONS)[number]['value'];

export const FUP_DATE_FILTER_OPTIONS = [
  { value: 'today_only', label: 'Somente os de hoje' },
  { value: 'exclude_today', label: 'Desconsiderar os de hoje (anteriores)' },
] as const;

export type OrchestratorFupDateFilter = (typeof FUP_DATE_FILTER_OPTIONS)[number]['value'];

export const DATE_PRESET_OPTIONS = [
  { value: DatePresetEnum.YESTERDAY, label: 'Ontem' },
  { value: DatePresetEnum.LAST_7_DAYS, label: 'Últimos 7 dias' },
  { value: DatePresetEnum.LAST_30_DAYS, label: 'Últimos 30 dias' },
  { value: DatePresetEnum.CUSTOM, label: 'Personalizado' },
] as const;

/* ─── Table: students_fup ───────────────────────────────── */

export interface StudentFup {
  id: number;
  student_id: number;
  workout_code: string;
  fup_summary: string | null;
  response: string | null;
  last_cycle_feedback: string | null;
  next_cycle_adjustments: string | null;
  weekly_availability: string | null;
  available_training_time: string | null;
  additional_notes: string | null;
  created_at: string;
  updated_at: string;
}

/* ─── Table: workout_assignments ────────────────────────── */

export interface WorkoutAssignment {
  id: number;
  student_id: number;
  competence: string;
  payment_history_id: number | null;
  current_training_code: string | null;
  next_training_code: string | null;
  expected_delivery_date: string | null;
  status: WorkoutStatus;
  delivered_at: string | null;
  delivered_by_portal_user_id: number | null;
  workout_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/* ─── App-level row (used by the component) ─────────────── */

export interface OrchestratorRow {
  id: number;
  workout_assignment_id: number | null;
  student_id: number;
  student_name: string;
  student_email: string | null;
  competence: string;

  payment_status: PaymentStatus;
  payment_method: string | null;
  is_single_payment: boolean | null;
  active_pause: ActiveSubscriptionPause | null;
  total_paused_days: number;
  payer_name: string | null;
  payer_email: string | null;
  payment_system: PaymentSystem | null;
  last_payment_date: string | null;
  paid_at: string | null;
  due_date: string | null;
  payment_history_id: number | null;

  fup_links: FupLink[];
  current_training_code: string | null;
  next_training_code: string | null;
  notes: string | null;
  student_notes: StudentNoteItem[];
  notes_summary: string | null;
  expected_delivery_date: string | null;
  workout_status: 'awaiting_fup_response' | 'ready_for_workout' | 'scheduled' | 'delivered' | 'canceled' | null;
  has_fup_response: boolean;
  student_fup_id: number | null;
  fup_workout_code: string | null;
  fup_created_at: string | null;
  coach_id: number | null;
  coach_name: string | null;
  joined_at?: string | null;
  is_new_student?: boolean;
  classification?: StudentClassification;
  last_workout_date?: string | null;
  project: ProjectIdentity | null;
  is_reactivated: boolean;
  cycle_fup_send_allowed: boolean;
  training_cycle?: 'monthly' | 'bimonthly';
  /** Dias corridos com ciclo ainda `scheduled` após a janela de 24 dias. */
  pending_performance_delivery_days?: number | null;
  has_consultancy_credit?: boolean;
  consultancy_credit_valid_until?: string | null;
  is_sensitive?: boolean;
  sensitive_reason?: string | null;
  is_pregnant?: boolean;
  pregnant_until?: string | null;
  is_red_flag?: boolean;
}

/* ─── Labels ────────────────────────────────────────────── */

export const WORKOUT_STATUS_LABELS: Record<WorkoutStatus, string> = {
  awaiting_payment: 'Aguardando pagamento',
  payment_failed: 'Pagamento vencido',
  ready_for_workout: 'Pronto para treino',
  awaiting_fup_response: 'Aguardando FUP',
  scheduled: 'Enviado para Performance',
  delivered: 'Entregue',
  canceled: 'Cancelado',
  paused: 'Assinatura pausada',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAID: 'Pago',
  PENDING: 'Pendente',
  FAILED: 'Falha',
  EXPIRED: 'Vencido',
  PAUSED: 'Pausada',
  SUBSCRIPTION_CANCELED: 'Assinatura cancelada',
  REFUNDED: 'Reembolsado',
  REMOVED: 'Removido',
  CANCELED: 'Cancelado',
};

export function getPaymentMethodLabel(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  return PAYMENT_METHOD_LABELS[value as keyof typeof PAYMENT_METHOD_LABELS] ?? value;
}

/* ─── View: student_latest_payment_history ──────────────── */

export interface StudentLatestPaymentView {
  student_id: number;
  student_name: string;
  student_email: string | null;
  payment_account_id: number;
  payment_system: PaymentSystem;
  external_customer_id: string;
  payment_history_id: number;
  value: number;
  status: string;
  external_payment_id: string;
  external_created_date: string;
  payment_created_at: string;
}
